import { type ShipmentRequirementDTO } from '@uturn/api/v1';
import { MetaDataContext } from '@uturn/portal/context';
import { ShipmentDetailContext } from '@uturn/portal/modules/shipments/providers/shipment-detail';
import type { FormValues } from '@uturn/portal/modules/shipments/schema';
import { isFieldReadOnly } from '@uturn/portal/modules/shipments/utils';
import {
	FormControl,
	FormField,
	FormItem,
	FormMessage,
	MultiSelect,
} from '@uturn/ui-kit';
import { useContext } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type ShippingRequirementsProps = {
	requirements: ShipmentRequirementDTO[];
};

export function ShippingRequirements({
	requirements,
}: ShippingRequirementsProps) {
	const { t } = useTranslation();
	const { shippingRequirementExceptions } = useContext(MetaDataContext);

	const { control } = useFormContext<FormValues>();

	const { readOnlyFields } = useContext(ShipmentDetailContext);

	return (
		<div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-6">
			<div className="sm:col-span-full">
				<h3 className="font-heading text-lg font-medium tracking-tight">
					{t(
						'pages.create_shipment.shipment_details.shipping_req.heading',
						'Shipping requirements',
					)}
				</h3>
			</div>
			<div className="sm:col-span-full">
				<FormField
					control={control}
					name="requirementIds"
					render={({ field }) => (
						<FormItem>
							<FormControl>
								<MultiSelect
									onChange={field.onChange}
									placeholder={t(
										'pages.create_shipment.shipment_details.shipping_req.placeholder',
										'Select requirements',
									).toString()}
									selected={field.value} // List of shippingRequirementExceptions.id
									options={requirements
										.filter((item) => !!item.id)
										.map((item) => {
											return {
												value: item.id as number, // Guarantied by Type Predicate Inference
												label: item.description ?? item.code ?? '-',
												disabled: item.code
													? shippingRequirementExceptions.includes(item.code)
													: false,
											};
										})}
									hideDisabledOptions={true}
									selectAllEnabled={false}
									align="start"
									searchPlaceholder={t(
										'pages.create_shipment.shipment_details.shipping_req.search.placeholder',
										'Search requirements...',
									).toString()}
									emptyMessage={t(
										'pages.create_shipment.shipment_details.shipping_req.search.empty',
										'No requirements found.',
									).toString()}
									disabled={isFieldReadOnly(readOnlyFields, 'requirementIds')}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
			</div>
		</div>
	);
}
